/**
 * 纪念日掠影第二版的画面层（P4d，design §11.11）：
 * - 路线光带：跟着光标一段段画出来的两条 Line2 粗线（同 constellation.ts 的做法——1 像素的
 *   THREE.Line 在 2 倍像素比下太细，加法混合、透明度也过不了辉光阈值 1.2）。一条 2.5 像素的芯、
 *   一圈 7 像素的光晕；周年、百天是金色，生日是淡玫瑰色。
 * - 光标：glow 贴图的 Sprite，钉在路线上的 u 处，参与辉光。
 * - 涟漪：到站（或光标经过只闪的站）时站上荡开一圈环，最多同时 3 个。
 * - 字：每站上方两行（主行、副行）的 DOM 标签，挂在 galaxy 的 labelLayer 下——不走 semantic.ts
 *   的标签池，也不受漫游那「淡到三成」影响；文字一律 textContent（回忆标题是用户写的）。
 * 路线、时间轴、镜头是纯逻辑，在 glimpse.ts；galaxy.ts 每帧把 u、镜头、淡入淡出、该显示的字喂过来。
 */
import * as THREE from 'three';
import { Line2 } from 'three/addons/lines/Line2.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import { el } from './dom.ts';
import { routePoint, type GlimpseRoute } from './glimpse.ts';
import type { Vec3 } from '@star-sea/shared';

const COLORS = { gold: 0xf3cf8b, rose: 0xff9dba };
// 光带：芯约 2.5 像素、光晕约 7 像素；透明度乘整场淡入淡出
const CORE_W = 2.5;
const HALO_W = 7;
const CORE_A = 0.7;
const HALO_A = 0.25;
/** 射线上每 0.4 个单位一个点；弧用路线每天的锚点 */
const RAY_STEP = 0.4;
/** 光标：glow 贴图的 Sprite，约 1.2 个单位 */
const CURSOR_R = 1.2;
/** 到站涟漪：ring 贴图，0.7 秒放大到约 2.5 个单位并淡掉；最多同时 3 个 */
const RIPPLE_S = 0.7;
const RIPPLE_R = 2.5;
const RIPPLE_N = 3;
/** 字：站的点往上抬这么多再投影；同时最多两个 */
const LABEL_LIFT = 1.2;
const LABEL_MAX = 2;
/** 投影在画面外（或镜头背后）时，改到画面中下方固定位置：水平居中、离底 22% */
const LABEL_FALLBACK = 0.78;

interface Ripple {
  sprite: THREE.Sprite;
  t0: number;
}

/** 各站当前该显示的字：stop 是第几站，k 是亮度（0–1，乘上整场 fade） */
export interface GlimpseLabel {
  stop: number;
  k: number;
}

export class GlimpseFx {
  private readonly group = new THREE.Group();
  private readonly labelLayer: HTMLElement;
  private readonly glow: THREE.Texture;
  private readonly ring: THREE.Texture;
  private readonly coreMat: LineMaterial;
  private readonly haloMat: LineMaterial;
  private core: Line2 | null = null;
  private halo: Line2 | null = null;
  private segments = 0;
  private readonly cursor: THREE.Sprite;
  private route: GlimpseRoute | null = null;
  private ripples: Ripple[] = [];
  private readonly labels = new Map<number, HTMLElement>();
  private ended = false;
  private time = 0;
  private readonly v = new THREE.Vector3();
  private readonly w = new THREE.Vector3();

  constructor(scene: THREE.Object3D, labelLayer: HTMLElement, textures: { glow: THREE.Texture; ring: THREE.Texture }) {
    this.labelLayer = labelLayer;
    this.glow = textures.glow;
    this.ring = textures.ring;
    this.group.renderOrder = 3;
    const material = (width: number): LineMaterial => {
      const m = new LineMaterial({ color: COLORS.gold, linewidth: width, transparent: true, opacity: 0 });
      m.depthTest = false;
      m.depthWrite = false;
      m.blending = THREE.AdditiveBlending;
      m.toneMapped = false;
      return m;
    };
    this.coreMat = material(CORE_W);
    this.haloMat = material(HALO_W);
    this.cursor = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.glow, blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false, transparent: true, opacity: 0 }));
    this.cursor.scale.setScalar(CURSOR_R);
    this.cursor.visible = false;
    this.group.add(this.cursor);
    scene.add(this.group);
  }

  /** 一条新路线：按它重建光带、定下这一趟的颜色；上一趟的东西清掉 */
  begin(route: GlimpseRoute): void {
    this.dispose();
    this.route = route;
    this.ended = false;
    const color = COLORS[route.color];
    this.coreMat.color.setHex(color);
    this.haloMat.color.setHex(color);
    (this.cursor.material as THREE.SpriteMaterial).color.setHex(color);
    const flat: number[] = [];
    const p: Vec3 = { x: 0, y: 0, z: 0 };
    if (route.kind === 'ray') {
      // 和 semantic.ts 的射线同一个公式，每 0.4 个单位一个点
      const n = Math.max(2, Math.ceil(route.length / RAY_STEP) + 1);
      for (let i = 0; i < n; i++) {
        routePoint(route, i / (n - 1), p);
        flat.push(p.x, p.y, p.z);
      }
    } else {
      // 弧：路线每天的锚点（只取 s0–s1 里的），两头补上精确的端点
      const { path, s0, s1 } = route.arc!;
      routePoint(route, 0, p);
      flat.push(p.x, p.y, p.z);
      for (let k = 0; k < path.arc.length; k++)
        if (path.arc[k]! > s0 && path.arc[k]! < s1) flat.push(path.anchors[k * 3]!, path.anchors[k * 3 + 1]!, path.anchors[k * 3 + 2]!);
      routePoint(route, 1, p);
      flat.push(p.x, p.y, p.z);
    }
    this.segments = Math.max(1, flat.length / 3 - 1);
    const make = (mat: LineMaterial): Line2 => {
      const geo = new LineGeometry();
      geo.setPositions(flat);
      const line = new Line2(geo, mat);
      line.computeLineDistances();
      line.frustumCulled = false;
      line.renderOrder = 3;
      line.geometry.instanceCount = 0;
      this.group.add(line);
      return line;
    };
    this.core = make(this.coreMat);
    this.halo = make(this.haloMat);
  }

  /** 每帧：u 是光标在路线上的位置，fade 是整场的淡入淡出，stops 是该显示的字（含淡入淡出的进度） */
  update(o: { time: number; dt: number; u: number; camera: THREE.Camera; width: number; height: number; fade: number; stops: GlimpseLabel[] }): void {
    this.time = o.time;
    const fade = Math.min(1, Math.max(0, o.fade));
    for (const m of [this.coreMat, this.haloMat]) m.resolution.set(o.width, o.height);
    this.coreMat.opacity = CORE_A * fade;
    this.haloMat.opacity = HALO_A * fade;
    // 光带跟着光标一段段画出来：同 ConstellationLines 的 reveal（最少留 1 段，免得 0 实例撞上驱动差异）
    const shown = Math.min(this.segments, Math.max(1, Math.round(o.u * this.segments)));
    for (const l of [this.core, this.halo]) if (l) l.geometry.instanceCount = shown;
    const route = this.route;
    if (route) {
      routePoint(route, o.u, this.v);
      this.cursor.position.copy(this.v);
      this.cursor.visible = fade > 0.002;
      (this.cursor.material as THREE.SpriteMaterial).opacity = 0.9 * fade;
    }
    for (const r of this.ripples) {
      const k = (o.time - r.t0) / RIPPLE_S;
      if (k >= 1) {
        r.sprite.visible = false;
        continue;
      }
      r.sprite.visible = true;
      r.sprite.scale.setScalar(0.35 + (RIPPLE_R - 0.35) * (1 - Math.pow(1 - k, 3)));
      (r.sprite.material as THREE.SpriteMaterial).opacity = (1 - k) * 0.85 * fade;
    }
    this.updateLabels(o);
  }

  /** 到站（或光标经过只闪的站）：站上荡开一圈涟漪；最多同时 RIPPLE_N 个，挤掉最旧的 */
  pulse(stopIndex: number): void {
    const route = this.route;
    if (!route || this.ended) return;
    const st = route.stops[stopIndex];
    if (!st) return;
    let r = this.ripples.find((x) => this.time - x.t0 >= RIPPLE_S);
    if (!r && this.ripples.length < RIPPLE_N) {
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({ map: this.ring, color: COLORS[route.color], blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false, transparent: true, opacity: 0 }),
      );
      sprite.renderOrder = 3;
      this.group.add(sprite);
      r = { sprite, t0: this.time };
      this.ripples.push(r);
    }
    if (!r) r = this.ripples.reduce((a, b) => (a.t0 < b.t0 ? a : b));
    routePoint(route, st.u, this.v);
    r.sprite.position.set(this.v.x, this.v.y + 0.2, this.v.z);
    r.sprite.visible = true;
    r.t0 = this.time;
  }

  /** 「收」或走完：不再放新的涟漪；光带、光标、字随 galaxy 给的 fade 淡掉 */
  end(): void {
    this.ended = true;
  }

  /** 释放这一趟的东西：线、涟漪、字；光标藏起来。材质留着，下次掠影还用 */
  dispose(): void {
    this.route = null;
    this.segments = 0;
    for (const l of [this.core, this.halo]) {
      if (!l) continue;
      this.group.remove(l);
      l.geometry.dispose();
    }
    this.core = this.halo = null;
    for (const r of this.ripples) {
      this.group.remove(r.sprite);
      (r.sprite.material as THREE.SpriteMaterial).dispose();
    }
    this.ripples = [];
    for (const [, elm] of this.labels) elm.remove();
    this.labels.clear();
    this.cursor.visible = false;
  }

  private updateLabels(o: { camera: THREE.Camera; width: number; height: number; fade: number; stops: GlimpseLabel[] }): void {
    const route = this.route;
    const keep = new Set<number>();
    if (route && o.fade > 0.002) {
      o.camera.updateMatrixWorld();
      for (const { stop, k } of o.stops.slice(0, LABEL_MAX)) {
        const st = route.stops[stop];
        if (!st || !st.label) continue;
        keep.add(stop);
        let elm = this.labels.get(stop);
        if (!elm) {
          elm = el(
            'div',
            { class: `label label-glimpse${route.color === 'rose' ? ' label-glimpse-rose' : ''}` },
            el('div', { class: 'gl-main', text: st.main }),
            el('div', { class: 'gl-sub', text: st.sub }),
          );
          this.labelLayer.appendChild(elm);
          this.labels.set(stop, elm);
        }
        // 站的点往上抬一点再投影；投在画面外、或在镜头背后（view z >= 0）时，改到画面中下方固定位置
        routePoint(route, st.u, this.v);
        this.v.y += LABEL_LIFT;
        const viewZ = this.w.copy(this.v).applyMatrix4(o.camera.matrixWorldInverse).z;
        this.v.project(o.camera);
        const on = viewZ < 0 && this.v.z < 1 && Math.abs(this.v.x) < 1.05 && Math.abs(this.v.y) < 1.05;
        const x = on ? (this.v.x * 0.5 + 0.5) * o.width : o.width / 2;
        const y = on ? (-this.v.y * 0.5 + 0.5) * o.height : o.height * LABEL_FALLBACK;
        elm.style.opacity = (k * o.fade).toFixed(2);
        elm.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -100%)`;
      }
    }
    for (const [i, elm] of this.labels) {
      if (keep.has(i)) continue;
      elm.remove();
      this.labels.delete(i);
    }
  }
}
